import { projectMutation } from '@/constants/mutations'
import service from '@/service'
import dedup from '@/store/dedup'
import urlManager from '@/urlManager'
import Vue from 'vue'

/**
 * This is the store module for Project
 */
export default {
  namespaced: true,
  getters: {
    projectById: state => id => state.projects[id],
    sectionById: state => id => state.sections[id],
    projectsByTeamId: state => teamId => state.teamProjects[teamId]
  },
  state: () => ({
    teamProjects: {},
    projects: {},
    sections: {}
  }),
  mutations: {
    [projectMutation.SET_TEAM_PROJECTS](state, { teamId, projects }) {
      state.teamProjects = { ...state.teamProjects, [teamId]: projects }
      projects.forEach(project => {
        Vue.set(state.projects, project.id, project)
      })
    },
    [projectMutation.UPDATE_PROJECT](state, { project }) {
      Vue.set(state.projects, project.id, project)
      const teamId = project.teamId
      const teamProjects = state.teamProjects[teamId]
      if (teamProjects) {
        const projectIndex = teamProjects.findIndex(p => p.id === project.id)
        teamProjects[projectIndex] = project
        Vue.set(state, 'teamProjects', { ...state.teamProjects })
      }
    },
    [projectMutation.DELETE_PROJECT](state, { projectId, teamId }) {
      Vue.delete(state.projects, projectId)
      let currentTeamProjects = state.teamProjects[teamId]
      if (currentTeamProjects) {
        currentTeamProjects = currentTeamProjects.filter(p => p.id !== projectId)
        const teamProjects = {
          ...state.teamProjects,
          [teamId]: currentTeamProjects
        }
        Vue.set(state, 'teamProjects', teamProjects)
      }
    },
    [projectMutation.ADD_PROJECT](state, { project }) {
      state.projects[project.id] = project
      const teamId = project.teamId
      const teamProjects = state.teamProjects[teamId]
      if (teamProjects) {
        teamProjects.push(project)
      } else {
        state.teamProjects[teamId] = [project]
      }
    },
    [projectMutation.SET_SECTIONS](state, { sections }) {
      sections.forEach(section => {
        Vue.set(state.sections, section.id, section)
      })
    },
    [projectMutation.UPDATE_SECTION](state, { section, sectionId }) {
      Vue.set(state.sections, sectionId, section)
    }
  },
  actions: {
    async getTeamProjects({ commit }, { teamId, orgId, params }) {
      const response = await service.client.get(urlManager.projectList(orgId), {
        params: {
          team_id: teamId,
          ...params
        }
      })
      commit(projectMutation.SET_TEAM_PROJECTS, { teamId, projects: response.data })
      return response.data
    },
    async getProject({ commit }, { projectId, orgId }) {
      const project = await dedup('getProject', { orgId, projectId }, async () => {
        const response = await service.client.get(urlManager.projectDetail(orgId, projectId))
        commit(projectMutation.UPDATE_PROJECT, { project: response.data })
        return response.data
      })
      return project
    },
    async getProjects({ commit }, { orgId }) {
      const response = await service.client.get(urlManager.projectList(orgId))
      const projects = response.data
      projects.forEach(project => commit(projectMutation.UPDATE_PROJECT, { project }))
      return projects
    },
    async deleteProject({ commit }, { projectId, orgId, teamId }) {
      const response = await service.client.delete(urlManager.projectDetail(orgId, projectId))
      commit(projectMutation.DELETE_PROJECT, { projectId, teamId })
      return response.data
    },
    async createProject({ commit }, { orgId, payload }) {
      const response = await service.client.post(urlManager.projectList(orgId), payload)
      commit(projectMutation.ADD_PROJECT, { project: response.data })
      return response.data
    },
    async updateProject({ commit }, { projectId, orgId, payload }) {
      const response = await service.client.patch(
        urlManager.projectDetail(orgId, projectId),
        payload
      )
      commit(projectMutation.UPDATE_PROJECT, { project: response.data })
      return response.data
    },
    async getSections({ commit }, { projectId, orgId }) {
      const response = await service.client.get(urlManager.sectionList(orgId), {
        params: {
          project_id: projectId
        }
      })
      commit(projectMutation.SET_SECTIONS, { sections: response.data })
      return response.data
    },
    async deleteSection(_, { sectionId, orgId, params }) {
      const response = await service.client.delete(urlManager.sectionDetail(orgId, sectionId), {
        params
      })
      return response.data
    },
    async createSection({ commit }, { orgId, payload }) {
      const response = await service.client.post(urlManager.sectionList(orgId), payload)
      commit(projectMutation.SET_SECTIONS, { sections: [response.data] })
      return response.data
    },
    async updateSection({ commit }, { sectionId, orgId, payload }) {
      const response = await service.client.patch(
        urlManager.sectionDetail(orgId, sectionId),
        payload
      )
      commit(projectMutation.UPDATE_SECTION, { section: response.data, sectionId })
      return response.data
    }
  }
}
